/**
 * @friskai/frisk-js Token Manager
 *
 * Manages access token (JWT) lifecycle:
 * - Fetches token using an API key
 * - Parses JWT to extract expiration
 * - Refreshes token if expired or on a periodic cadence
 *
 * This is a singleton class that uses setInterval for background refresh.
 */

import {
  FriskBaseURLNotFoundError,
  FriskInvalidAPIKeyError,
  InvalidAccessTokenError,
  MissingBaseURLError,
  UnexpectedFriskServerResponseError,
} from '../errors'
import { captureError } from '../errors/capture-error'
import { FriskSDKLoggingOptions, SDKLogger } from '../logging'
import { deriveSdkLogger } from '../logging/derive-sdk-logger'

/**
 * Callback function type for token update notifications
 */
export type TokenUpdateCallback = (token: string) => void

/**
 * Options for AccessTokenProvider initialization
 */
export interface AccessTokenProviderOptions {
  /**
   * API key for authentication
   */
  apiKey: string

  /**
   * Base URL for the Frisk API
   */
  baseUrl: string

  /**
   * Refresh interval in minutes (minimum 1 minute)
   * @default 5
   */
  refreshIntervalMinutes?: number

  /**
   * Callbacks to invoke when token is updated
   */
  callbacks?: Iterable<TokenUpdateCallback>

  logging?: FriskSDKLoggingOptions
}

/**
 * AccessTokenProvider - Singleton class for managing Frisk access tokens
 *
 * Handles token fetching, caching, and automatic refresh using setInterval.
 */
export class AccessTokenProvider {
  private static instance: AccessTokenProvider | null = null
  private static instanceKey: string | null = null

  private tokenUrl: string
  private apiKey: string
  private refreshIntervalMs: number
  private accessToken: string | null = null
  private expEpochS: number | null = null
  private lastRefreshEpochS: number | null = null
  private callbacks: TokenUpdateCallback[] = []
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null
  private safetyMarginS = 30 // Proactively refresh if exp is within 30s
  private isRefreshing = false
  private logger: SDKLogger

  /**
   * Private constructor - use getInstance() instead
   */
  private constructor(options: AccessTokenProviderOptions) {
    this.logger = deriveSdkLogger(options.logging, {
      component: 'AccessTokenProvider',
    })

    if (!options.baseUrl) {
      throw new MissingBaseURLError()
    }

    // Construct token URL
    const baseUrl = options.baseUrl.endsWith('/')
      ? options.baseUrl.slice(0, -1)
      : options.baseUrl
    this.tokenUrl = `${baseUrl}/api/agent_tokens/issue`

    this.apiKey = options.apiKey

    // Minimum 60 seconds to avoid thrashing
    const refreshMinutes = Math.max(1, options.refreshIntervalMinutes ?? 5)
    this.refreshIntervalMs = refreshMinutes * 60 * 1000

    if (options.callbacks) {
      this.callbacks = [...options.callbacks]
    }
  }

  /**
   * Get or create the singleton AccessTokenProvider instance
   *
   * @param options - AccessTokenProvider options (required on first call)
   * @returns The singleton AccessTokenProvider instance
   */
  static getInstance(
    options?: AccessTokenProviderOptions,
  ): AccessTokenProvider {
    if (options) {
      const key = `${options.apiKey}:${options.baseUrl}`

      if (
        AccessTokenProvider.instance &&
        AccessTokenProvider.instanceKey !== key
      ) {
        // Different configuration - warn but return existing instance
        AccessTokenProvider.instance.logger.warn?.(
          '[AccessTokenProvider] Instance already exists with different configuration. Returning existing instance.',
        )
      }

      if (!AccessTokenProvider.instance) {
        AccessTokenProvider.instance = new AccessTokenProvider(options)
        AccessTokenProvider.instanceKey = key
      }
    }

    if (!AccessTokenProvider.instance) {
      throw new Error(
        'AccessTokenProvider not initialized. Call getInstance() with options first.',
      )
    }

    return AccessTokenProvider.instance
  }

  /**
   * Check if the singleton instance exists
   */
  static hasInstance(): boolean {
    return AccessTokenProvider.instance !== null
  }

  /**
   * Reset the singleton instance (mainly for testing)
   */
  static resetInstance(): void {
    if (AccessTokenProvider.instance) {
      AccessTokenProvider.instance.stopBackgroundRefresh()
    }
    AccessTokenProvider.instance = null
    AccessTokenProvider.instanceKey = null
  }

  /**
   * Fetch a new access token from the Frisk API
   */
  async fetchAccessToken(): Promise<string> {
    try {
      const response = await fetch(this.tokenUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        let errorDetails: string | Record<string, unknown> | null = null
        try {
          errorDetails = (await response.json()) as Record<string, unknown>
        } catch {
          errorDetails = await response.text()
        }

        if (response.status === 401 || response.status === 403) {
          throw new FriskInvalidAPIKeyError(
            `API key rejected: ${JSON.stringify(errorDetails)}`,
          )
        }

        throw new UnexpectedFriskServerResponseError(
          `HTTP ${response.status}: ${JSON.stringify(errorDetails)}`,
        )
      }

      let data: { access_token?: string }
      try {
        data = (await response.json()) as { access_token?: string }
      } catch {
        throw new UnexpectedFriskServerResponseError(
          'Failed to parse JSON response',
        )
      }

      const token = data.access_token
      if (typeof token !== 'string' || !token) {
        throw new UnexpectedFriskServerResponseError(
          "Issuer response missing 'access_token' string",
        )
      }

      return token
    } catch (error) {
      if (
        error instanceof FriskInvalidAPIKeyError ||
        error instanceof UnexpectedFriskServerResponseError
      ) {
        throw error
      }

      // Network error
      throw new FriskBaseURLNotFoundError(
        `Failed to reach Frisk API: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Add a callback to be invoked when token is updated
   */
  addCallback(callback: TokenUpdateCallback): void {
    this.callbacks.push(callback)
  }

  /**
   * Remove a callback
   */
  removeCallback(callback: TokenUpdateCallback): void {
    const index = this.callbacks.indexOf(callback)
    if (index !== -1) {
      this.callbacks.splice(index, 1)
    }
  }

  /**
   * Decode JWT payload without verifying signature
   */
  private decodeJwtPayload(token: string): Record<string, unknown> {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) {
        throw new InvalidAccessTokenError('JWT must have 3 parts')
      }

      const payloadB64 = parts[1]
      // Fix base64url to base64
      const base64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
      // Add padding if needed
      const padding = '='.repeat((4 - (base64.length % 4)) % 4)
      const payloadJson = atob(base64 + padding)

      return JSON.parse(payloadJson)
    } catch (error) {
      if (error instanceof InvalidAccessTokenError) {
        throw error
      }
      throw new InvalidAccessTokenError(
        `Failed to decode JWT: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  /**
   * Update the cached token and notify callbacks
   */
  private updateCachedToken(token: string): void {
    const payload = this.decodeJwtPayload(token)
    const exp = payload.exp

    if (typeof exp !== 'number') {
      throw new InvalidAccessTokenError('JWT missing exp claim')
    }

    const now = Math.floor(Date.now() / 1000)
    const oldToken = this.accessToken

    this.accessToken = token
    this.expEpochS = exp
    this.lastRefreshEpochS = now

    // Notify callbacks if token changed
    if (oldToken !== token) {
      for (const callback of this.callbacks) {
        try {
          callback(token)
        } catch (error) {
          const wrappedError = new Error(
            'AccessTokenProvider: Error processing callback',
            { cause: error },
          )
          captureError(wrappedError, this.logger)
        }
      }
    }
  }

  /**
   * Check if token needs refresh
   */
  private needsRefresh(nowS: number): boolean {
    // If no token yet, we need one
    if (!this.accessToken || this.expEpochS === null) {
      return true
    }

    // If token expired or exp is near, refresh early
    if (this.expEpochS - nowS <= this.safetyMarginS) {
      return true
    }

    // Periodic refresh window reached
    const lastRefresh = this.lastRefreshEpochS ?? 0
    if (nowS - lastRefresh >= this.refreshIntervalMs / 1000) {
      return true
    }

    return false
  }

  /**
   * Get a valid access token, refreshing if necessary
   */
  async getAccessToken(): Promise<string> {
    const now = Math.floor(Date.now() / 1000)

    if (this.needsRefresh(now) && !this.isRefreshing) {
      this.isRefreshing = true
      try {
        const token = await this.fetchAccessToken()
        this.updateCachedToken(token)
      } finally {
        this.isRefreshing = false
      }
    }

    if (!this.accessToken) {
      throw new FriskInvalidAPIKeyError('No access token available')
    }

    return this.accessToken
  }

  /**
   * Get the current cached token without refreshing
   * Returns null if no token is cached
   */
  getCachedToken(): string | null {
    return this.accessToken
  }

  /**
   * Get token expiry epoch seconds if known
   */
  getTokenExpiry(): number | null {
    return this.expEpochS
  }

  /**
   * Force a token refresh regardless of expiry/interval
   */
  async forceRefresh(): Promise<string> {
    this.isRefreshing = true
    try {
      const token = await this.fetchAccessToken()
      this.updateCachedToken(token)
      return token
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Start background token refresh using setInterval
   */
  startBackgroundRefresh(): void {
    if (this.refreshIntervalId !== null) {
      return // Already running
    }

    // Refresh every 5 seconds, but only actually fetch if needed
    const checkIntervalMs = 5000

    this.refreshIntervalId = setInterval(async () => {
      try {
        await this.getAccessToken()
      } catch (error) {
        const wrapped = new Error(
          'AccessTokenProvider: Background refresh failed',
          {
            cause: error,
          },
        )
        captureError(wrapped, this.logger)
      }
    }, checkIntervalMs)

    // Don't prevent Node.js from exiting
    if (this.refreshIntervalId.unref) {
      this.refreshIntervalId.unref()
    }
  }

  /**
   * Stop background token refresh
   */
  stopBackgroundRefresh(): void {
    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId)
      this.refreshIntervalId = null
    }
  }

  /**
   * Check if background refresh is running
   */
  isBackgroundRefreshRunning(): boolean {
    return this.refreshIntervalId !== null
  }
}
